/**
 * Schémas Zod du module utilisateurs : adhésions, profil et invitations.
 * Référence : document 06 § 6.3 (contrats d'entrée).
 */
import { z } from 'zod';
import { INVITATION_STATUSES, MEMBERSHIP_STATUSES, THEME_PREFERENCES } from '../enums/auth.enum.js';
import { TENANT_AGENCY_MAX } from '../constants/company.constants.js';
import { normalizeFirstName, normalizeLastName } from '../utils/format.utils.js';
import { emailSchema } from './auth.schema.js';
import { includeSchema, paginationQuerySchema, searchSchema, uuidSchema } from './common.schema.js';
/**
 * Statuts d'adhésion qu'un administrateur de société peut fixer.
 * `invited` et `revoked` ne sont jamais posés à la main : ils découlent du parcours
 * d'invitation et du retrait d'adhésion.
 */
export const MANAGEABLE_MEMBERSHIP_STATUSES = ['active', 'suspended'];
const optionalText = (max) => z.string().trim().max(max).nullable().optional();
/** Champs d'identité : la casse saisie est normalisée (nom en capitales, prénom capitalisé). */
const optionalFirstName = optionalText(80).transform(normalizeFirstName);
const optionalLastName = optionalText(80).transform(normalizeLastName);
const optionalCountryCode = z
    .string()
    .trim()
    .toUpperCase()
    .length(2, { message: 'Le code pays doit contenir 2 lettres.' })
    .nullable()
    .optional();
const optionalSocialSecurityNumber = z
    .string()
    .trim()
    .regex(/^[0-9 ]{13,20}$/, {
    message: 'Le numero de securite sociale doit contenir entre 13 et 20 chiffres (espaces autorises).',
})
    .optional();
/** Paramètres de `GET /users`. */
export const memberListQuerySchema = paginationQuerySchema.extend({
    search: searchSchema,
    include: includeSchema,
    filter: z
        .object({
        status: z.enum(MEMBERSHIP_STATUSES).optional(),
        roleId: uuidSchema.optional(),
    })
        .optional(),
});
/** Corps de `PATCH /users/:id` : au moins un champ à modifier. */
export const memberUpdateSchema = z
    .object({
    email: emailSchema.optional(),
    googleAccountEmail: emailSchema.nullable().optional(),
    firstName: optionalFirstName,
    lastName: optionalLastName,
    phone: optionalText(30),
    addressLine1: optionalText(180),
    addressLine2: optionalText(180),
    postalCode: optionalText(20),
    city: optionalText(120),
    countryCode: optionalCountryCode,
    socialSecurityNumber: optionalSocialSecurityNumber,
    /** Rôle principal de l'adhésion. */
    roleId: uuidSchema.optional(),
    jobTitle: optionalText(80),
    status: z.enum(MANAGEABLE_MEMBERSHIP_STATUSES).optional(),
    /** Rôles complémentaires ; l'ensemble fourni remplace l'existant. */
    additionalRoleIds: z.array(uuidSchema).max(20).optional(),
    /** Agences rattachées au membre ; l'ensemble fourni remplace l'existant. */
    agencyIds: z.array(uuidSchema).max(TENANT_AGENCY_MAX).optional(),
})
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'Aucun champ à mettre à jour.',
});
/** Corps de `PATCH /users/me` : profil personnel (aucune permission requise). */
export const profileUpdateSchema = z
    .object({
    firstName: optionalFirstName,
    lastName: optionalLastName,
    phone: optionalText(30),
    addressLine1: optionalText(180),
    addressLine2: optionalText(180),
    postalCode: optionalText(20),
    city: optionalText(120),
    countryCode: optionalCountryCode,
    locale: optionalText(10),
    timezone: optionalText(64),
    themePreference: z.enum(THEME_PREFERENCES).optional(),
})
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'Aucun champ à mettre à jour.',
});
/** Corps de `POST /invitations`. */
export const invitationCreateSchema = z.object({
    email: emailSchema,
    roleId: uuidSchema,
});
/** Paramètres de `GET /invitations`. */
export const invitationListQuerySchema = paginationQuerySchema.extend({
    search: searchSchema,
    filter: z.object({ status: z.enum(INVITATION_STATUSES).optional() }).optional(),
});
/** Corps de `POST /invitations/accept` : jeton reçu par l'invité. */
export const invitationAcceptSchema = z.object({
    token: z.string().trim().min(16).max(200),
});
//# sourceMappingURL=user.schema.js.map